import { useEffect } from 'react'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { func, object } from 'prop-types'
import { t } from '@lingui/core/macro'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { useAppSelector } from '@redux/hooks'
import { Trans } from '@lingui/react/macro'

export const SFTP = ({
  updatedAccount,
  setUpdatedAccount,
  setRequiredFieldMissing,
  newConnection,
  getHelperText,
}) => {
  const [USE_AIMIA_FOR_SFTP_PUSH] = useAppSelector(
    selectFeatureIncludes([FeatureDict.USE_AIMIA_FOR_SFTP_PUSH]),
  )

  const checkForSFTPErrorsHidden = () => {
    newConnection &&
      setRequiredFieldMissing(
        !updatedAccount.metadata.host ||
          !updatedAccount.metadata.username ||
          (!updatedAccount.metadata.password && !updatedAccount.metadata.client_secret) ||
          !updatedAccount.metadata.port,
      )
  }

  const checkForSFTPErrors = () => {
    newConnection
      ? setRequiredFieldMissing(
          !updatedAccount.metadata.host ||
            !updatedAccount.metadata.username ||
            (!updatedAccount.metadata.password && !updatedAccount.metadata.client_secret) ||
            !updatedAccount.metadata.port,
        )
      : setRequiredFieldMissing(
          !updatedAccount.metadata.host ||
            !updatedAccount.metadata.username ||
            !updatedAccount.metadata.port,
        )
  }

  const handlePasswordChange = e => {
    const password = e.target.value
    setUpdatedAccount({
      ...updatedAccount,
      metadata: {
        ...updatedAccount.metadata,
        password,
        client_secret: password ? '' : updatedAccount.metadata.client_secret,
        auth_type: 'password',
      },
    })
  }

  const handlePrivateKeyChange = e => {
    const client_secret = e.target.value
    setUpdatedAccount({
      ...updatedAccount,
      metadata: {
        ...updatedAccount.metadata,
        client_secret,
        password: client_secret ? '' : updatedAccount.metadata.password,
        auth_type: 'secret_key',
      },
    })
  }

  const handleFocus = field => {
    if (
      field === 'password' &&
      updatedAccount.metadata.client_secret &&
      updatedAccount.metadata.password
    ) {
      setUpdatedAccount({
        ...updatedAccount,
        metadata: {
          ...updatedAccount.metadata,
          client_secret: '',
        },
      })
    } else if (
      field === 'client_secret' &&
      updatedAccount.metadata.password &&
      updatedAccount.metadata.client_secret
    ) {
      setUpdatedAccount({
        ...updatedAccount,
        metadata: {
          ...updatedAccount.metadata,
          password: '',
        },
      })
    }
  }

  useEffect(() => {
    checkForSFTPErrors()
    checkForSFTPErrorsHidden()
  }, [updatedAccount.metadata])

  return (
    <>
      <div>
        <TextField
          style={{ width: '100%', marginBottom: 12 }}
          variant="outlined"
          required
          label={t`Host`}
          InputLabelProps={{ shrink: true }}
          value={updatedAccount.metadata.host}
          onChange={e => {
            setUpdatedAccount({
              ...updatedAccount,
              metadata: { ...updatedAccount.metadata, host: e.target.value },
            })
          }}
          helperText={getHelperText(!updatedAccount.metadata.host)}
        />
      </div>
      <div>
        <TextField
          style={{ width: '100%', marginBottom: 12 }}
          variant="outlined"
          required
          label={t`Username`}
          InputLabelProps={{ shrink: true }}
          value={updatedAccount.metadata.username}
          onChange={e => {
            setUpdatedAccount({
              ...updatedAccount,
              metadata: { ...updatedAccount.metadata, username: e.target.value },
            })
          }}
          helperText={getHelperText(!updatedAccount.metadata.username)}
        />
      </div>
      <div style={{ marginBottom: 15 }}>
        <TextField
          style={{ width: '100%', marginBottom: 12 }}
          variant="outlined"
          required={newConnection && !updatedAccount.metadata.client_secret}
          type="password"
          label={t`Password`}
          InputLabelProps={{ shrink: true }}
          value={updatedAccount.metadata.password}
          onChange={handlePasswordChange}
          onFocus={() => handleFocus('password')}
          sx={
            updatedAccount.metadata.client_secret
              ? {
                  '& .MuiInputBase-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }
              : {}
          }
          helperText={
            updatedAccount.metadata.client_secret
              ? t`Password is inactive while private key is set.`
              : getHelperText(
                  newConnection &&
                    !updatedAccount.metadata.password &&
                    !updatedAccount.metadata.client_secret,
                )
          }
        />
        <Typography variant="caption">
          <Trans>
            Passwords are securely stored and never surfaced to the user. Entering a new value will
            override the existing password.
          </Trans>
        </Typography>
      </div>
      {USE_AIMIA_FOR_SFTP_PUSH && (
        <div style={{ marginBottom: 15 }}>
          <TextField
            style={{ width: '100%', marginBottom: 12 }}
            variant="outlined"
            required={newConnection && !updatedAccount.metadata.password}
            type="password"
            label={t`Private Key`}
            InputLabelProps={{ shrink: true }}
            value={updatedAccount.metadata.client_secret || ''}
            onChange={handlePrivateKeyChange}
            onFocus={() => handleFocus('client_secret')}
            sx={
              updatedAccount.metadata.password
                ? {
                    '& .MuiInputBase-root': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }
                : {}
            }
            helperText={
              updatedAccount.metadata.password
                ? t`Private key is inactive while password is set.`
                : getHelperText(
                    newConnection &&
                      !updatedAccount.metadata.client_secret &&
                      !updatedAccount.metadata.password,
                  )
            }
          />
          <Typography variant="caption">
            <Trans>
              The path to your private key file. This will override password authentication if
              provided.
            </Trans>
          </Typography>
        </div>
      )}
      <div>
        <TextField
          style={{ width: '100%', marginBottom: 12 }}
          variant="outlined"
          required
          label={t`Port`}
          InputLabelProps={{ shrink: true }}
          value={updatedAccount.metadata.port}
          onChange={e => {
            setUpdatedAccount({
              ...updatedAccount,
              metadata: { ...updatedAccount.metadata, port: e.target.value },
            })
          }}
          helperText={getHelperText(!updatedAccount.metadata.port)}
        />
      </div>
    </>
  )
}

SFTP.propTypes = {
  updatedAccount: object.isRequired,
  setUpdatedAccount: func.isRequired,
  setRequiredFieldMissing: func.isRequired,
  newConnection: object.isRequired,
  getHelperText: func.isRequired,
}
